import { useEffect } from 'react';
import { Button, Group, Modal, NumberInput, SegmentedControl, Select, Stack, Text, Textarea } from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { ServerSearchSelect } from './ServerSearchSelect';
import type { ExpenseType } from './ExpenseCategoriesModal';
import { monthToDate, thisMonth, ym } from '../lib/month';

const CATEGORIES = ['SALARY', 'RENT', 'UTILITY', 'SUPPLY', 'TAX', 'MAINTENANCE', 'OTHER'] as const;
type Category = (typeof CATEGORIES)[number];

/**
 * Xarajat qo'shish oynasi — Xarajatlar sahifasi va Kassa hisoboti uchun BITTA joy
 * (maydonlar, tekshiruvlar va maosh uchun xodim tanlash ikkala joyda bir xil bo'lsin).
 *
 * `forMonth` — oyna ochilganda qo'yiladigan "qaysi oy uchun" (sukut: joriy oy).
 * `spentAt` — xarajat sanasi (ISO). Berilmasa server hozirgi vaqtni yozadi; kassada
 * o'tgan kun tanlangan bo'lsa o'sha kun beriladi, aks holda xarajat bugunga tushardi.
 */
export function ExpenseFormModal({
  opened, onClose, forMonth, spentAt,
}: {
  opened: boolean;
  onClose: () => void;
  forMonth?: string;
  spentAt?: string;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  // To'lov usullari — Moliya (to'lovlar) sahifasi bilan AYNI yorliqlar
  const METHODS = [
    { value: 'CASH', label: t('paymentsPage.methodCash') },
    { value: 'CARD', label: t('paymentsPage.methodCard') },
    { value: 'TRANSFER', label: t('paymentsPage.methodTransfer') },
    { value: 'OTHER', label: t('paymentsPage.methodOther') },
  ];

  // Xarajatlar sahifasi bilan AYNI so'rov (kesh umumiy). Formada faqat FAOL turlar.
  const { data: types } = useQuery({
    queryKey: ['expense-categories'],
    queryFn: async () => (await api.get<ExpenseType[]>('/expenses/categories')).data,
    staleTime: 60_000,
  });
  const typeList = types ?? [];
  const activeTypes = typeList.filter((c) => c.isActive);
  // Turlar hali yaratilmagan (seed skripti ishlamagan) bo'lsa — eski enum rejimi
  // saqlanadi, aks holda admin umuman xarajat qo'sha olmay qolardi.
  const useTypes = typeList.length > 0;
  const typeById = new Map(typeList.map((c) => [c.id, c]));

  const form = useForm({
    initialValues: { category: 'SALARY' as Category, categoryId: '', amount: 0, userId: '', note: '', forMonth: forMonth || thisMonth(), method: 'CASH' },
    validate: {
      amount: (v) => (v <= 0 ? t('expenses.amountRequired') : null),
    },
  });

  // Har ochilishda: oy — chaqiruvchi bergan, tur rejimida bo'sh tanlov qolmasin
  useEffect(() => {
    if (!opened) return;
    form.setFieldValue('forMonth', forMonth || thisMonth());
    if (useTypes && !typeById.has(form.values.categoryId)) {
      form.setFieldValue('categoryId', activeTypes[0]?.id ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, forMonth, useTypes]);

  // Maosh maydoni (xodim tanlash) turning GURUHIGA qarab ochiladi: tur rejimida
  // enum qiymati backendda `legacyCategory` dan olinadi.
  const selectedType = (form.values.categoryId ? typeById.get(form.values.categoryId) : undefined)
    ?? (useTypes ? activeTypes[0] : undefined)
    ?? null;
  const isSalary = useTypes
    ? selectedType?.legacyCategory === 'SALARY'
    : form.values.category === 'SALARY';

  const createMut = useMutation({
    mutationFn: (v: typeof form.values) => {
      // T11: tur tanlangan bo'lsa FAQAT `categoryId` yuboriladi — enum `category`
      // qiymatini backend turning `legacyCategory` sidan oladi (yagona manba).
      // Zaxira: tanlov bo'sh qolgan bo'lsa (turlar ro'yxati kech kelgan) formada
      // KO'RINIB turgan birinchi faol tur olinadi.
      const type = (v.categoryId ? typeById.get(v.categoryId) : undefined)
        ?? (useTypes ? activeTypes[0] : undefined);
      const salary = type ? type.legacyCategory === 'SALARY' : v.category === 'SALARY';
      return api.post('/expenses', {
        ...(type ? { categoryId: type.id } : { category: v.category }),
        amount: v.amount,
        method: v.method,
        userId: salary && v.userId ? v.userId : undefined,
        note: v.note || undefined,
        forMonth: v.forMonth || undefined,
        spentAt: spentAt || undefined,
      });
    },
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('expenses.added') });
      // Xarajat ko'rinadigan hamma joy: Xarajatlar sahifasi, moliya kartalari, kassa
      qc.invalidateQueries({ queryKey: ['expenses'] });
      qc.invalidateQueries({ queryKey: ['expense-detail'] });
      qc.invalidateQueries({ queryKey: ['expenses-summary'] });
      qc.invalidateQueries({ queryKey: ['finance'] });
      qc.invalidateQueries({ queryKey: ['cashbook'] });
      form.reset();
      // Tur rejimida ro'yxatning birinchi FAOL turi qayta tanlanadi (reset bo'shatib qo'yadi)
      form.setFieldValue('categoryId', activeTypes[0]?.id ?? '');
      onClose();
    },
  });

  return (
    <Modal opened={opened} onClose={onClose} title={t('expenses.add')}>
      <form onSubmit={form.onSubmit((v) => createMut.mutate(v))}>
        <Stack>
          {/* T11: tur ro'yxati bo'sh bo'lsa (skript ishlamagan) eski enum tanlagichi
              qoladi — admin baribir xarajat qo'sha oladi. */}
          {useTypes ? (
            // Qiymat BO'SH qolmasin: turlar so'rovdan keyin kelsa ham birinchi faol tur
            // ko'rinadi va AYNI shu qiymat yuboriladi (`createMut` bilan bir xil zaxira).
            <Select
              label={t('expenses.type')}
              description={t('expenses.typeHint')}
              allowDeselect={false}
              searchable
              data={activeTypes.map((c) => ({ value: c.id, label: c.name }))}
              value={form.values.categoryId || activeTypes[0]?.id || null}
              onChange={(v) => form.setFieldValue('categoryId', v ?? '')}
            />
          ) : (
            <Select
              label={t('expenses.category')} allowDeselect={false}
              data={CATEGORIES.map((c) => ({ value: c, label: t(`expenseCat.${c}`) }))}
              {...form.getInputProps('category')}
            />
          )}
          {isSalary && (
            <ServerSearchSelect
              label={t('expenses.staff')}
              placeholder={t('expenses.staffSearch')}
              endpoint="/staff"
              mapOption={(u: { id: string; fullName: string; role: string }) => ({ value: u.id, label: u.fullName })}
              value={form.values.userId || null}
              onChange={(v) => form.setFieldValue('userId', v || '')}
            />
          )}
          <NumberInput label={t('expenses.amount')} min={0} step={100000} thousandSeparator=" " suffix={` ${t('currency.som')}`} required {...form.getInputProps('amount')} />
          {/* Kassa hisoboti chiqimni shu bo'yicha naqd / bank ga ajratadi */}
          <Stack gap={4}>
            <Text size="sm" fw={500}>{t('paymentsPage.method')}</Text>
            <SegmentedControl fullWidth data={METHODS} {...form.getInputProps('method')} />
            <Text size="xs" c="dimmed">{t('expenses.methodHint')}</Text>
          </Stack>
          {/* T9: forMonth DOIM "YYYY-MM" bo'lib yuboriladi (`lib/month.ym`) */}
          <MonthPickerInput
            label={t('expenses.forMonth')}
            value={monthToDate(form.values.forMonth)}
            onChange={(d) => form.setFieldValue('forMonth', d ? ym(d) : '')}
            valueFormat="YYYY MMMM" clearable
          />
          <Textarea label={t('expenses.note')} autosize minRows={2} {...form.getInputProps('note')} />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>{t('common.cancel')}</Button>
            <Button type="submit" loading={createMut.isPending}>{t('common.save')}</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
